import React, { useCallback, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme, DescendingGraphIcon } from '@spoiler-alert/ui-library';
import { DesirabilityStrings } from '../../string-resources';
import Tracking from '../../utilities/tracking-apis';
import DesirabilityEvents from './desirability-events';
import { type Desirability, desirabilityReasons, isDesirabilityFlagged } from './desirability-presenter';

const useStyles = createUseStyles(
  {
    panel: {
      padding: 12,
      borderRadius: 4,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: Theme.grey5,
      border: `1px solid ${Theme.grey30}`,
    },
    headingRow: {
      fontWeight: 500,
      fontSize: 14,
      marginBottom: 8,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    whatsThis: {
      color: Theme.teal,
      fontWeight: 400,
      '&:hover': {
        color: Theme.tealDark,
      },
    },
    reasonRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '4px 8px',
      lineHeight: '20px',
      fontWeight: 400,
    },
    reasonValue: {
      fontWeight: 400,
      whiteSpace: 'nowrap',
    },
    line: {
      borderBottom: `1px solid ${Theme.grey30}`,
    },
  },
  { name: 'DesirabilityPanel' },
);

interface DesirabilityPanelProps {
  desirability?: Desirability | null;
}

const DesirabilityPanel = ({ desirability }: DesirabilityPanelProps) => {
  const classes = useStyles();
  const reasons = desirabilityReasons(desirability);
  const trackedHover = useRef(false);
  const trackedWhatsThis = useRef(false);

  // Fire once per mount, like the indicator: we want viewers, not repeated hovers or clicks.
  const trackHover = useCallback(() => {
    if (trackedHover.current) return;
    trackedHover.current = true;
    Tracking.Event(DesirabilityEvents.hoveredPanel);
  }, []);

  const trackWhatsThis = useCallback(() => {
    if (trackedWhatsThis.current) return;
    trackedWhatsThis.current = true;
    Tracking.Event(DesirabilityEvents.clickedWhatsThis);
  }, []);

  if (!isDesirabilityFlagged(desirability)) return null;

  return (
    <div className={classes.panel} data-testid="desirability-panel" onMouseEnter={trackHover}>
      <div className={classes.headingRow}>
        <div>
          <DescendingGraphIcon style={{ marginRight: 8 }} />
          {DesirabilityStrings.heading}
        </div>
        <a
          className={classes.whatsThis}
          data-testid="desirability-whats-this"
          href={DesirabilityStrings.whatsThisUrl}
          rel="noopener noreferrer"
          target="_blank"
          onClick={trackWhatsThis}
        >
          {DesirabilityStrings.whatsThis}
        </a>
      </div>
      {reasons.map((reason, index) => (
        <div className={`${classes.reasonRow} ${index !== reasons.length - 1 ? classes.line : ''}`} key={reason.key}>
          <span data-testid="desirability-reason-label">{reason.label}</span>
          <span className={classes.reasonValue}>{reason.value}</span>
        </div>
      ))}
    </div>
  );
};

export default DesirabilityPanel;
